import styled from '@emotion/styled';
import { Badge, Button, Stack, Text, tokens } from '@hotjar/hotsauce';
import { moment } from '@hotjar/insights-common/lib/moment';
import { useTranslation } from '@hotjar/translations';
import { scopesOptions } from '../../domains/public-api';
import type { PublicApiKey } from '../../domains/public-api';

const Wrapper = styled.div`
  position: relative;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};

  .items {
    margin-top: ${tokens.spacer.x1pt5};

    .key {
      width: 120px;
    }
  }
`;

const RemoveButton = styled(Button)`
  position: absolute;
  top: ${tokens.spacer.x1};
  right: ${tokens.spacer.x1};
`;

const S = {
  Stack: styled(Stack)`
    align-items: center;
  `,
};

type Props = { data: PublicApiKey; onDelete: (data: PublicApiKey) => void };

export const DetailCard = ({ data, onDelete }: Props): JSX.Element => {
  const { keyId, displayName, organizationId, organizationName, scopes, lastUsed } = data;
  const { t } = useTranslation();

  return (
    <Wrapper>
      <Text textType="subtitle1">{displayName}</Text>
      <Stack className="items" direction="column" spacing="x1">
        <S.Stack direction="row">
          <Text as="span" textType="subtitle2" textColor="text_light" className="key">
            {t('auth:public-api.list.organization')}
          </Text>
          <Text as="span" textType="body2">
            {organizationName}
          </Text>
          <Badge
            kind="custom"
            size="s"
            color={tokens.color.ui_disabled}
            textColor={tokens.color.text_light}
          >
            {`${t('common:id')}: ${organizationId}`}
          </Badge>
        </S.Stack>
        <Stack direction="row">
          <Text as="span" textType="subtitle2" textColor="text_light" className="key">
            {t('auth:public-api.client-id')}
          </Text>
          <Text as="span" textType="body2">
            {keyId}
          </Text>
        </Stack>
        <Stack direction="row">
          <Text as="span" textType="subtitle2" textColor="text_light" className="key">
            {t('auth:public-api.scope')}
          </Text>
          <Text as="span" textType="body2">
            {scopesOptions
              .filter((scope) => scopes.includes(scope.value))
              .map((scope) => scope.label)
              .join(', ')}
          </Text>
        </Stack>
        <Stack direction="row">
          <Text as="span" textType="subtitle2" textColor="text_light" className="key">
            {t('auth:public-api.list.last-time-used')}
          </Text>
          <Text as="span" textType="body2">
            {lastUsed ? moment.unix(lastUsed).fromNow() : t('auth:public-api.list.never-used')}
          </Text>
        </Stack>
      </Stack>
      <RemoveButton onClick={() => onDelete(data)} kind="danger_secondary" size="s" icon="trash">
        {t('auth:public-api.list.delete-button')}
      </RemoveButton>
    </Wrapper>
  );
};
